import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Organization } from 'sdk';
import { Step, Stepper } from '~/modules/common/stepper/stepper';
import { InvitationsStep } from '~/modules/home/onboarding/invitations-step';
import { getOnboardingSteps } from '~/modules/home/onboarding/onboarding-steps';
import { SkipStepButton } from '~/modules/home/onboarding/skip-step-button';
import { WelcomeText } from '~/modules/home/onboarding/welcome-text';
import { meInvitationsQueryOptions } from '~/modules/me/query';
import { CreateOrganizationForm } from '~/modules/organization/create-organization-form';
import { organizationsListQueryOptions } from '~/modules/organization/query';
import { Card, CardContent, CardDescription, CardHeader } from '~/modules/ui/card';
import { InviteUsers } from '~/modules/user/invite-users';
import { UpdateUserForm } from '~/modules/user/update-user-form';
import { useCurrentUser } from '~/modules/user/user-store';
import { flattenInfiniteData } from '~/query/basic/flatten';

export type OnboardingState = 'start' | 'stepper' | 'completed';

interface OnboardingProps {
  state: OnboardingState;
  onStart: () => void;
  onComplete: () => void;
}

export function Onboarding({ state, onStart, onComplete }: OnboardingProps) {
  const user = useCurrentUser();
  const { t } = useTranslation();

  const [organization, setOrganization] = useState<Organization | null>(null);

  const orgQuery = useInfiniteQuery(organizationsListQueryOptions({ relatableUserId: user.id }));
  const organizations = flattenInfiniteData<Organization>(orgQuery.data);
  const { data: invitationsData } = useQuery(meInvitationsQueryOptions());
  const invitations = invitationsData?.items ?? [];

  // Locked at mount, after the welcome route has loaded both queries: an answer or a new organization mid-flow keeps the steps.
  const [steps] = useState(() => getOnboardingSteps({ hasOrganizations: organizations.length > 0, hasInvitations: invitations.length > 0 }));

  const skipButton = <SkipStepButton onComplete={onComplete} />;

  return (
    <div className="flex min-h-[90svh] flex-col items-center sm:min-h-svh">
      <div className="my-auto w-full">
        {state === 'start' && <WelcomeText invitations={invitations} onStart={onStart} />}
        {state === 'stepper' && (
          <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 py-8 transition-[opacity,translate,scale] duration-500 ease-out sm:w-10/12 starting:translate-y-4 starting:scale-95 starting:opacity-0">
            {steps.length === 1 && <h2 className="flex justify-center text-lg font-semibold">{steps[0].label}</h2>}
            <Stepper steps={steps} onClickStep={(step, setStep) => setStep(step)} onComplete={onComplete}>
              {steps.map(({ description, label, id }) => (
                <Step key={id} label={label} checkIcon={id === 'organization' && !organization ? XIcon : undefined}>
                  <Card>
                    {description && (
                      <CardHeader>
                        <CardDescription>{description}</CardDescription>
                      </CardHeader>
                    )}
                    <CardContent>
                      {id === 'invitations' && <InvitationsStep>{skipButton}</InvitationsStep>}
                      {id === 'profile' && (
                        <UpdateUserForm user={user} compact>
                          {skipButton}
                        </UpdateUserForm>
                      )}
                      {id === 'organization' && !organization && (
                        <CreateOrganizationForm
                          callback={(args) => {
                            if (args.status === 'success') setOrganization(args.data);
                          }}
                        >
                          {skipButton}
                        </CreateOrganizationForm>
                      )}
                      {id === 'organization' && organization && <p className="text-muted-foreground text-sm">{t('c:already_created_org.text')}</p>}
                      {id === 'invitation' && organization && (
                        <InviteUsers channel={organization} mode="email">
                          {skipButton}
                        </InviteUsers>
                      )}
                      {id === 'invitation' && !organization && (
                        <div>
                          <p className="text-muted-foreground mb-4 text-sm">{t('c:need_org_to_invite.text')}</p>
                          {skipButton}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Step>
              ))}
            </Stepper>
          </div>
        )}
      </div>
    </div>
  );
}
